import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Send as SendIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  category: string;
  message: string;
}

const ContactUs: React.FC = () => {
  const { mode } = useThemeMode();
  const { user } = useAuth();
  const recaptchaRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState<ContactFormData>({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    category: 'general',
    message: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  const categories = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'billing', label: 'Billing & Subscriptions' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'partnership', label: 'Partnership Opportunity' },
  ];

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please correct the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      if (window.grecaptcha && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        try {
          console.log('Executing reCAPTCHA...');
          recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'contact_form' });
          console.log('reCAPTCHA token generated:', recaptchaToken ? 'SUCCESS' : 'FAILED');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without token:', recaptchaError);
        }
      } else {
        console.log('reCAPTCHA not available:', {
          grecaptcha: !!window.grecaptcha,
          siteKeyConfigured: !!process.env.REACT_APP_RECAPTCHA_SITE_KEY
        });
      }

      // Send contact form via Supabase Edge Function
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      }).catch((fetchError) => {
        console.error('Fetch error:', fetchError);
        throw new Error(`Network error: ${fetchError.message}`);
      });

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Success
      setSubmitted(true);
      toast.success('Thank you for your message! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        subject: '',
        category: 'general',
        message: '',
      });

    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to send message: ${errorMessage}. Please try again or email us directly at support@health-tree.com`);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    // Add global error handlers for debugging
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection in ContactUs:', event.reason);
      event.preventDefault(); // Prevent the default browser error handling
    };

    const handleError = (event: ErrorEvent) => {
      console.error('Global error in ContactUs:', event.error);
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Load reCAPTCHA script
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        console.log('reCAPTCHA already loaded');
        return;
      }

      console.log('Loading reCAPTCHA...');
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('reCAPTCHA script loaded successfully');
        // Wait a bit for grecaptcha to be available
        setTimeout(() => {
          try {
            if (window.grecaptcha) {
              console.log('reCAPTCHA is ready');
              setRecaptchaReady(true);
            } else {
              console.error('reCAPTCHA failed to initialize');
            }
          } catch (error) {
            console.error('Error checking reCAPTCHA status:', error);
          }
        }, 1000);
      };
      script.onerror = (error) => {
        console.error('Failed to load reCAPTCHA script:', error);
      };
      document.head.appendChild(script);
    };

    // Only load if we have a site key
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha();
    } else {
      console.warn('reCAPTCHA site key not configured');
    }

    // Cleanup event listeners
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Card
          sx={{
            textAlign: 'center',
            p: 4,
            background: mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
            border: mode === 'dark' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(16, 185, 129, 0.2)',
          }}
        >
          <SupportIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Message Sent! 📧
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for reaching out to us. We've received your message and will get back to you within 24 hours.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSubmitted(false)}
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': {
                borderColor: '#059669',
                backgroundColor: 'rgba(16, 185, 129, 0.05)',
              },
            }}
          >
            Send Another Message
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          Contact Us
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Have a question, suggestion, or need help? We're here to support you on your health journey.
        </Typography>
      </Box>

      <Grid container spacing={6}>
        {/* Contact Information */}
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Get in Touch
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </Typography>
          </Box>

          {/* Contact Cards */}
          <Box sx={{ space: 3 }}>
            <Card
              sx={{
                p: 3,
                mb: 3,
                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ color: '#10b981', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Email
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                support@health-tree.com
              </Typography>
              <Typography variant="caption" color="text.secondary">
                We typically respond within 24 hours
              </Typography>
            </Card>

            <Card
              sx={{
                p: 3,
                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ color: '#10b981', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Office
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Remote-first company
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Serving customers worldwide
              </Typography>
            </Card>
          </Box>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              p: 4,
              background: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Send us a Message
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </Grid>

                {/* Category */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      label="Category"
                      onChange={(e) => handleInputChange('category', e.target.value)}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Subject */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    required
                  />
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    error={!!errors.message}
                    helperText={errors.message || 'Please provide as much detail as possible'}
                    required
                  />
                </Grid>

                {/* reCAPTCHA Notice */}
                <Grid item xs={12}>
                  <Alert 
                    severity={recaptchaReady ? "success" : "info"} 
                    sx={{ mb: 2 }}
                  >
                    {recaptchaReady 
                      ? "✓ This form is protected by reCAPTCHA v3 (invisible protection active)"
                      : "This form is protected by reCAPTCHA to prevent spam. Your privacy is protected by Google's Privacy Policy and Terms of Service."
                    }
                  </Alert>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      backgroundColor: '#10b981',
                      '&:hover': {
                        backgroundColor: '#059669',
                      },
                      py: 1.5,
                      px: 4,
                      fontWeight: 'bold',
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* reCAPTCHA container */}
      <div ref={recaptchaRef} />
    </Container>
  );
};

export default ContactUs;